import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { BehaviorSubject, Observable, map, of, tap } from 'rxjs';
import { Categories, HomeData } from 'src/app/models/homeData';
import { BagResponse } from 'src/app/models/bagResponse';
import { globalSearchResponse } from 'src/app/models/globalSearchResponse';

@Injectable({
  providedIn: 'root'
})
export class HomeService {
  homeData: BehaviorSubject<HomeData | null> =
    new BehaviorSubject<HomeData | null>(null);

  shimmer: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private categoriesCache: Categories[] | null = null;
  private bannersCache: any[] | null = null;
  private dashboardCache: any[] | null = null;
  filtersCache: any | null = null;

  constructor(private readonly http: HttpClient) { }

  getHomeData(): Observable<HomeData> {
    return this.http
      .get<{
        data: HomeData;
      }>(`${environment.base_url}user/dashboard`)
      .pipe(map(res => res.data));
  }

  getAds(): Observable<any> {
    return this.http.get(`${environment.base_url}user/advertisement-popup`).pipe(map((res: any) => res.data));
  }

  getHomeSections(query: string = ''): Observable<any> {
    const queryString = query && !query.startsWith('?') ? `?${query}` : query;
    return this.http.get(`${environment.base_url}user/dashboard/home-section${queryString}`).pipe(map((res: any) => res.data));
  }

  getCategories(page: number = 1, limit: number = 12, sort: string = 'landing', showOnLanding: boolean = false): Observable<Categories[]> {
    // If requesting page 1 and cache exists, return cache
    let query = `?page=${page}&limit=${limit}`;
    if (sort && !showOnLanding) {
      query += `&pageType=${sort}`;
    }
    if (showOnLanding) {
      query += `&showOnLandingPage=${showOnLanding}`;
    }
    if (page === 1 && this.categoriesCache && sort === 'landing') {
      return of(this.categoriesCache);
    }
    return this.http
      .get<{ data: Categories[] }>(`${environment.base_url}User/categories${query}`)
      .pipe(
        map((res: any) => res.data as Categories[]),
        tap((data) => {
          // Only cache the first page
          if (page === 1) {
            this.categoriesCache = data;
          }
        })
      );
  }

  getSubCategoriesData(page: number = 1, limit: number = 12, categoryId: string): Observable<Categories[]> {
    let query = `?page=${page}&limit=${limit}`;
    if (categoryId) {
      query += `&id=${categoryId}`;
    }
    return this.http
      .get<{ data: Categories[] }>(`${environment.base_url}User/subCategory${query}`)
      .pipe(map((res: any) => res.data as Categories[]));
  }

  getVendorPortfolio(vendorId: string): Observable<Categories[]> {
    return this.http
      .get<{ data: Categories[] }>(`${environment.base_url}User/vendor/${vendorId}/portfolio`)
      .pipe(
        map((res: any) => res.data)
      );
  }

  getBanners(): Observable<any[]> {
    if (this.bannersCache) {
      return of(this.bannersCache);
    }
    return this.http
      .get<{ data: any[] }>(`${environment.base_url}User/banners`)
      .pipe(
        map((res: any) => res.data as any[]),
        tap((data) => (this.bannersCache = data))
      );
  }

  getPopularBrand(page: number = 1, limit: number = 10, showOnLandingPage = false, vendorId: string = ''): Observable<any> {
    let query = `?page=${page}&limit=${limit}`;
    if (showOnLandingPage) {
      query += `&showOnLandingPage=true`;
    }
    if (vendorId) {
      query += `&vendorId=${vendorId}`;
    }
    return this.http.get(`${environment.base_url}User/brands${query}`).pipe(map((res: any) => res.data));
  }

  getFilters(selectedBody: any) {
    if (this.filtersCache) {
      return of(this.filtersCache);
    }
    return this.http
      .get<{ data: any[] }>(`${environment.base_url}User/products/filters`)
      .pipe(
        map((res: any) => res.data as any[]),
        tap((data) => (this.filtersCache = data))
      );
  }

  getRecommendations() {
    return this.http.get(`${environment.base_url}User/recommendations`).pipe(map((res: any) => res.data));
  }

  getDashboardData() {
    if (this.dashboardCache) {
      return of(this.dashboardCache);
    }
    return this.http
      .get<{ data: Categories[] }>(`${environment.base_url}User/dashboard/new`)
      .pipe(
        map((res: any) => res.data as any[]),
        tap((data) => (this.dashboardCache = data))
      );
  }

  getSubCategories(categoryId: string): Observable<Categories[]> {
    return this.http
      .get<{
        data: Categories[];
      }>(`${environment.base_url}user/subCategoryDropdown/${categoryId}`)
      .pipe(map(res => res.data));
  }

  subscribeToEmail(data: { email: string }): Observable<BagResponse> {
    return this.http
      .post<{
        data: BagResponse;
      }>(`${environment.base_url}user/subscribeEmail`, data)
      .pipe(map(res => res.data));
  }

  globalearch(query: string): Observable<globalSearchResponse> {
    return this.http
      .get<{
        data: globalSearchResponse;
      }>(`${environment.base_url}user/globalSearch${query}`)
      .pipe(map(res => res.data));
  }

  recentSearch(): Observable<globalSearchResponse> {
    return this.http
      .get<{
        data: globalSearchResponse;
      }>(`${environment.base_url}user/recentSearch`)
      .pipe(map(res => res.data));
  }

  deleteRecentSearch(): Observable<globalSearchResponse> {
    return this.http
      .delete<{
        data: globalSearchResponse;
      }>(`${environment.base_url}user/deleteRecentSearch`)
      .pipe(map(res => res.data));
  }

  downloadInvoice(orderId: string): Observable<any> {
    return this.http.get(`${environment.operational_url}operational/invoice/${orderId}`);
  }
}
